import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAsyncData } from '@/hooks';
import { api } from '@/api/client';
import type { AdminTask } from '@/types';
import { AsyncPanel } from '@/components/platform/AsyncPanel';
import { Card } from '@/components/ui/Card';
import styles from './AdminTasksPage.module.css';

const PRIORITY_TONES: Record<string, string> = {
  HIGH: styles.priorityHigh,
  MEDIUM: styles.priorityMedium,
  LOW: styles.priorityLow,
};

const STATUS_CLASSES: Record<string, string> = {
  OPEN: styles.statusOpen,
  IN_PROGRESS: styles.statusInProgress,
  COMPLETED: styles.statusCompleted,
  BLOCKED: styles.statusBlocked,
};

const COMPLETABLE = new Set(['OPEN', 'IN_PROGRESS']);

function formatDate(ts: string): string {
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return ts;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return ts;
  }
}

export function AdminTasksPage() {
  const { t } = useTranslation();

  const { state, reload, replace } = useAsyncData<AdminTask[]>(
    'admin-tasks',
    () => api.admin.tasks.list(),
    true,
  );

  const handleComplete = useCallback(async (taskId: string) => {
    try {
      await api.admin.tasks.complete(taskId);
      if (state.status === 'ready' && state.data) {
        replace(state.data.map((t) => (t.id === taskId ? { ...t, status: 'COMPLETED' } : t)));
      }
    } catch {
      reload();
    }
  }, [state, replace, reload]);

  return (
    <div className={styles.page}>
      <div className="page-header">
        <h1>{t('admin.tasks.title')}</h1>
        <p className="page-subtitle">{t('admin.tasks.subtitle')}</p>
      </div>

      <Card>
        <AsyncPanel
          state={state}
          onRetry={reload}
          isEmpty={(data) => data.length === 0}
          emptyTitle={t('admin.tasks.noTasks')}
        >
          {(data) => (
            <div className="table-scroll">
              <table className={styles.table}>
              <thead className={styles.tableHead}>
                <tr>
                  <th>{t('admin.tasks.taskTitle')}</th>
                  <th>{t('admin.tasks.category')}</th>
                  <th>{t('admin.tasks.priority')}</th>
                  <th>{t('admin.tasks.status')}</th>
                  <th>{t('admin.tasks.created')}</th>
                  <th>{/* actions */}</th>
                </tr>
              </thead>
              <tbody>
                {data.map((task) => (
                  <tr key={task.id} className={styles.tableRow}>
                    <td className={styles.tableCell}>
                      {task.targetUrl ? (
                        <a href={task.targetUrl} className={styles.taskTitleLink}>
                          {task.title}
                        </a>
                      ) : (
                        <span className={styles.taskTitle}>{task.title}</span>
                      )}
                    </td>
                    <td className={styles.tableCell}>
                      <span className={styles.taskCategory}>{task.category}</span>
                    </td>
                    <td className={styles.tableCell}>
                      <span className={`${styles.priorityBadge} ${PRIORITY_TONES[task.priority] ?? ''}`}>
                        {t(`admin.tasks.${task.priority.toLowerCase()}`)}
                      </span>
                    </td>
                    <td className={styles.tableCell}>
                      <span className={`${styles.statusBadge} ${STATUS_CLASSES[task.status] ?? ''}`}>
                        {t(`admin.tasks.${task.status === 'IN_PROGRESS' ? 'inProgress' : task.status.toLowerCase()}`)}
                      </span>
                    </td>
                    <td className={styles.tableCell}>
                      <span className={styles.createdDate}>{formatDate(task.createdAt)}</span>
                    </td>
                    <td className={styles.tableCell}>
                      {COMPLETABLE.has(task.status) && (
                        <button type="button" className={styles.completeBtn} onClick={() => handleComplete(task.id)}>
                          {t('admin.tasks.complete')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          )}
        </AsyncPanel>
      </Card>
    </div>
  );
}
